import { useCallback, useMemo } from 'react'
import { Drawer } from '@base-ui/react/drawer'
import { useIsPresent } from 'framer-motion'
import { QueuedMessagesPanel, useActiveChatId } from '@renderer/chat'
import { MessageList } from '@renderer/chat/components'
import { useClaimViewSheetOpen, useViewSheetLayer } from '../../../atoms'
import { useStagedActionsAdmin } from '../../../hooks/use-staged-actions'
import { useViewActions } from '../../../view-atoms'
import { ScrollButton } from '../../view-scroll-button'
import { useViewBodyBottomInset } from '../../view'
import { ChatQuestionsFloatingBarSlot } from '../../../views/chat-view/question-floating-bar'
import { useConversationPendingAnswerables } from '../../../views/chat-view/use-conversation-pending-answerables'
import { ChatShareControl } from '../chat-share-control'
import { ChatMessageListSkeleton } from '../chat-message-list-skeleton'
import { useConversationChatDrawer } from './use-conversation-chat-drawer'
import { useSheetDismissGesture } from './use-sheet-dismiss-gesture'

const SHEET_SCROLL_BUTTON_GAP_PX = 8

interface ConversationChatDrawerProps {
  chatId: string
  open: boolean
  entranceSettled: boolean
  isFreshSubmit?: boolean
  onFreshSubmitSettled?: () => void
  onDismiss: () => void
  onStagedActionClick: (actionId: string) => void
  sourceLabel: string
}

export function ConversationChatDrawer({
  chatId,
  open,
  entranceSettled,
  isFreshSubmit,
  onFreshSubmitSettled,
  onDismiss,
  onStagedActionClick,
  sourceLabel,
}: ConversationChatDrawerProps) {
  const container = useViewSheetLayer()
  // Exiting views remain mounted for their dissolve, but their portals must not
  // leave a second drawer in the shared sheet layer.
  const isActiveEntry = useIsPresent()
  useClaimViewSheetOpen(open && isActiveEntry)
  // Stable identity: `ChatShareWrapper` is memoized, and a fresh object every
  // render would defeat it.
  const shareEventProps = useMemo(() => ({ source: 'chat-drawer', surface: sourceLabel }), [sourceLabel])
  const dismissGesture = useSheetDismissGesture(onDismiss)

  if (!container || !isActiveEntry) {
    return null
  }

  return (
    <Drawer.Root
      open={open}
      modal={false}
      disablePointerDismissal
      onOpenChange={(next) => {
        if (!next) {
          onDismiss()
        }
      }}
    >
      <Drawer.Portal container={container}>
        {/* Untinted: it only exists so clicking the view behind the sheet dismisses it. */}
        <Drawer.Backdrop onClick={onDismiss} className="pointer-events-auto absolute inset-0" />
        <Drawer.Viewport className="absolute inset-0">
          <Drawer.Popup
            initialFocus={false}
            finalFocus={false}
            // Popout: match the composer's `mx-1` inset, pad the content by the
            // layer's overshoot so the thread ends above the composer, and cast a
            // real shadow since there's no card edge to sit the sheet against.
            className="bg-background app:inset-x-1 app:pb-6 app:shadow-[0px_-8px_32px_rgba(0,0,0,0.18)] corner-smooth pointer-events-auto absolute inset-x-0 bottom-0 top-[64px] flex flex-col rounded-t-2xl border-x border-t shadow-[0px_-4px_16px_rgba(0,0,0,0.08)] outline-none transition-transform duration-300 ease-out data-[ending-style]:translate-y-full data-[starting-style]:translate-y-full motion-reduce:transition-none"
            {...dismissGesture}
          >
            {/* The row is the swipe surface; the handle is a button, which Base
                UI excludes from mouse swipes, so it clicks to close instead. */}
            <div className="relative flex h-10 shrink-0 cursor-grab select-none items-center justify-center active:cursor-grabbing">
              <button
                type="button"
                aria-label="Close chat drawer"
                className="corner-squircle group flex h-6 w-14 cursor-pointer items-center justify-center rounded-[var(--radius-size-sm)]"
                onClick={onDismiss}
              >
                {/* Box height matches the bars so the flat state centers on the
                    button's midline. Pivoting the outer tips sags the vertex by
                    19px * sin(10deg) ~= 3.3px, so lift the pair by half of that
                    to keep the chevron centered on the same midline. */}
                <span
                  className="relative h-1 w-9 opacity-20 transition-[opacity,transform] duration-150 ease-out group-hover:-translate-y-[1.65px] group-hover:opacity-40 motion-reduce:transform-none motion-reduce:transition-none"
                  aria-hidden="true"
                >
                  <span className="bg-foreground absolute left-0 top-0 h-1 w-[19px] origin-left rounded-full transition-transform duration-150 ease-out group-hover:rotate-[10deg] motion-reduce:transform-none motion-reduce:transition-none" />
                  <span className="bg-foreground absolute right-0 top-0 h-1 w-[19px] origin-right rounded-full transition-transform duration-150 ease-out group-hover:-rotate-[10deg] motion-reduce:transform-none motion-reduce:transition-none" />
                </span>
              </button>
              <div className="absolute inset-y-0 right-2 flex items-center">
                <ChatShareControl chatId={chatId} eventProps={shareEventProps} />
              </div>
            </div>
            <Drawer.Content className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <ConversationChatThread
                chatId={chatId}
                open={open}
                entranceSettled={entranceSettled}
                isFreshSubmit={isFreshSubmit}
                onFreshSubmitSettled={onFreshSubmitSettled}
                onStagedActionClick={onStagedActionClick}
                sourceLabel={sourceLabel}
              />
            </Drawer.Content>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

/** The thread a conversation drawer holds, for surfaces that show it in place instead (a meeting's Chat tab). */
export function ConversationChatThread({
  chatId,
  open,
  entranceSettled,
  isFreshSubmit,
  onFreshSubmitSettled,
  onStagedActionClick,
  sourceLabel,
}: Omit<ConversationChatDrawerProps, 'onDismiss'>) {
  const drawer = useConversationChatDrawer({
    chatId,
    open,
    entranceSettled,
    isFreshSubmit,
    onFreshSubmitSettled,
  })
  const { openView } = useViewActions()
  const { invalidateStagedActions } = useStagedActionsAdmin()
  const activeChatId = useActiveChatId()
  const hasPendingQuestion = useConversationPendingAnswerables(activeChatId).length > 0
  // The covered floating bar's inset would open a gap above the in-flow question card.
  const floatingBarInset = useViewBodyBottomInset()
  const paddingBottom = hasPendingQuestion ? SHEET_SCROLL_BUTTON_GAP_PX : floatingBarInset
  const messageListStyle = useMemo(() => ({ paddingBottom }), [paddingBottom])
  const { showScrollToEnd, scrollToEnd, isLoadingMessages } = drawer
  const handleScrollToEnd = useCallback(() => scrollToEnd(), [scrollToEnd])
  const handleReminderClick = useCallback(
    (reminderId: string) => openView('reminder', { reminderId }, { sourceLabel }),
    [openView, sourceLabel],
  )

  return (
    <>
      {isLoadingMessages ? (
        <ChatMessageListSkeleton />
      ) : (
        <MessageList
          className="flex-1"
          style={messageListStyle}
          onStagedActionClick={onStagedActionClick}
          onStagedActionCreated={invalidateStagedActions}
          onReminderClick={handleReminderClick}
        />
      )}
      {hasPendingQuestion ? (
        // The sheet covers the floating bar, so the thread's deferred question card lives here.
        <div className="relative flex shrink-0 px-3 pb-3">
          <div
            className="pointer-events-none absolute inset-x-0 bottom-full z-20 flex justify-start px-3"
            style={{ paddingBottom: SHEET_SCROLL_BUTTON_GAP_PX }}
          >
            <ScrollButton show={showScrollToEnd} onClick={handleScrollToEnd} />
          </div>
          <ChatQuestionsFloatingBarSlot source="chat-drawer" />
        </div>
      ) : (
        <div
          className="pointer-events-none absolute inset-x-0 z-20 flex justify-start px-3"
          style={{ bottom: SHEET_SCROLL_BUTTON_GAP_PX }}
        >
          <ScrollButton show={showScrollToEnd} onClick={handleScrollToEnd} />
        </div>
      )}
      <QueuedMessagesPanel chatId={chatId} />
    </>
  )
}
